import * as RadixSelect from '@radix-ui/react-select'
import type { LucideIcon } from 'lucide-react'
import { cn } from '../../lib/cn'

/**
 * Select on the Radix primitive — the one shadcn/ui wraps — styled to match
 * the deck's input treatment. A native <select> renders an OS dropdown on
 * Windows and a wheel on iOS; this is one consistent popover everywhere,
 * with keyboard navigation and type-ahead for the fourteen schools.
 */
export function Select({
  value,
  onValueChange,
  options,
  placeholder = 'Select…',
  id,
  invalid,
  icon: Icon,
}: {
  value: string
  onValueChange: (value: string) => void
  options: readonly string[]
  placeholder?: string
  id?: string
  invalid?: boolean
  icon?: LucideIcon
}) {
  return (
    <RadixSelect.Root value={value || undefined} onValueChange={onValueChange}>
      <RadixSelect.Trigger
        id={id}
        aria-invalid={invalid}
        className={cn(
          'flex w-full min-h-11 items-center justify-between gap-2 rounded-xl border border-line',
          'bg-surface py-2.5 pr-3.5 text-left text-ink transition-colors',
          Icon ? 'pl-9' : 'pl-3.5',
          'data-[placeholder]:text-muted-3',
          'focus:outline-none focus-visible:bg-white focus-visible:border-teal focus-visible:ring-3 focus-visible:ring-teal/15',
          'data-[state=open]:bg-white data-[state=open]:border-teal data-[state=open]:ring-3 data-[state=open]:ring-teal/15',
          'aria-invalid:border-orange aria-invalid:bg-orange-soft/40',
          'relative',
        )}
      >
        {Icon && (
          <Icon
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-3"
            strokeWidth={2}
            aria-hidden
          />
        )}
        {/* min-w-0 so a long school name truncates instead of pushing the chevron out. */}
        <span className="min-w-0 flex-1 truncate">
          <RadixSelect.Value placeholder={placeholder} />
        </span>
        <RadixSelect.Icon className="shrink-0 text-muted">
          <svg width="11" height="7" viewBox="0 0 10 6" fill="none" aria-hidden>
            <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </RadixSelect.Icon>
      </RadixSelect.Trigger>

      <RadixSelect.Portal>
        <RadixSelect.Content
          position="popper"
          sideOffset={6}
          className={cn(
            'z-60 overflow-hidden rounded-2xl border border-line bg-white shadow-lift',
            'w-(--radix-select-trigger-width)',
            'max-h-[min(340px,var(--radix-select-content-available-height))]',
          )}
        >
          <RadixSelect.ScrollUpButton className="flex h-6 items-center justify-center text-[9px] text-muted">
            ▲
          </RadixSelect.ScrollUpButton>

          <RadixSelect.Viewport className="p-1.5">
            {options.map((option) => (
              <RadixSelect.Item
                key={option}
                value={option}
                className={cn(
                  'flex min-h-11 cursor-pointer select-none items-center justify-between gap-2',
                  'rounded-xl px-2.5 py-2.5 text-sm leading-snug text-ink outline-none',
                  'data-[highlighted]:bg-surface-2 data-[highlighted]:text-navy-950',
                  'data-[state=checked]:font-bold data-[state=checked]:text-teal',
                )}
              >
                <RadixSelect.ItemText>{option}</RadixSelect.ItemText>
                <RadixSelect.ItemIndicator className="shrink-0 text-teal">
                  <svg width="13" height="10" viewBox="0 0 13 10" fill="none" aria-hidden>
                    <path
                      d="M1 5l3.5 3.5L12 1"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </RadixSelect.ItemIndicator>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>

          <RadixSelect.ScrollDownButton className="flex h-6 items-center justify-center text-[9px] text-muted">
            ▼
          </RadixSelect.ScrollDownButton>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  )
}
